import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReceiptText } from 'lucide-react';
import {
  PayableDueState,
  PayableSettlement,
  PERMISSIONS,
  type PayableInvoiceSummary,
} from '@optiflow/types';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { usePayableInvoicesQuery, usePayableSuppliersQuery } from '../api/payables.api';
import { DueStateBadge, InvoiceDocumentBadge, SettlementBadge } from '../components/PayableBadges';

const PAGE_SIZE = 20;

/**
 * Supplier invoices with their settlement worked out.
 *
 * Only what is still owed, unless someone asks otherwise — the job this list exists for is deciding
 * what to pay next, and a page full of settled invoices buries it. Every derived figure on a row is
 * the server's: what is outstanding, whether it is late, and which ageing bucket it falls in.
 */
export function PayableInvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches } = useBranch();
  const { format } = useCurrency();
  const { can } = usePermissions();
  const [params] = useSearchParams();

  const [search, setSearch] = useState('');
  const [supplierId, setSupplierId] = useState(params.get('supplierId') ?? '');
  const [branchId, setBranchId] = useState(params.get('branchId') ?? '');
  const [settlement, setSettlement] = useState<PayableSettlement | ''>('');
  const [due, setDue] = useState<PayableDueState | ''>('');
  const [outstandingOnly, setOutstandingOnly] = useState(true);
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [page, setPage] = useState(1);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = usePayableInvoicesQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    supplierId,
    branchId,
    settlement,
    due,
    outstandingOnly,
  });

  // Suppliers this user may see owed money, which needs no purchasing capability of its own.
  const suppliers = usePayableSuppliersQuery({ branchId });
  const canPay = can(PERMISSIONS.PAYABLES_PAY);

  const onFilter =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value);
      setPage(1);
    };

  const columns: Column<PayableInvoiceSummary>[] = [
    {
      key: 'reference',
      header: t('payables.fields.reference'),
      primary: true,
      render: (invoice) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {invoice.reference}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {invoice.supplierName}
          </span>
        </span>
      ),
    },
    {
      key: 'supplierInvoiceNumber',
      header: t('payables.fields.supplierInvoiceNumber'),
      render: (invoice) => (
        <span className="text-muted font-mono text-sm" dir="ltr">
          {invoice.supplierInvoiceNumber}
        </span>
      ),
    },
    {
      key: 'branch',
      header: t('payables.fields.branch'),
      render: (invoice) => (
        <span className="text-muted text-sm" dir="auto">
          {invoice.branchName}
        </span>
      ),
    },
    {
      key: 'dates',
      header: t('payables.fields.dueDate'),
      render: (invoice) => (
        <span className="flex flex-col">
          <span className="text-ink text-sm" dir="ltr">
            {invoice.dueDate}
          </span>
          <span className="text-muted text-xs" dir="ltr">
            {t('payables.issuedOn', { date: invoice.invoiceDate })}
          </span>
        </span>
      ),
    },
    {
      key: 'total',
      header: t('payables.fields.invoiceTotal'),
      align: 'end',
      render: (invoice) => (
        <span className="text-muted" dir="ltr">
          {format(invoice.obligationMinor)}
        </span>
      ),
    },
    {
      key: 'allocated',
      header: t('payables.fields.allocated'),
      align: 'end',
      render: (invoice) => (
        <span className="text-muted" dir="ltr">
          {format(invoice.activeAllocatedMinor)}
        </span>
      ),
    },
    {
      key: 'outstanding',
      header: t('payables.fields.outstanding'),
      align: 'end',
      render: (invoice) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(invoice.outstandingMinor)}
        </span>
      ),
    },
    {
      key: 'settlement',
      header: t('payables.fields.settlement'),
      render: (invoice) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <SettlementBadge settlement={invoice.settlement} />
          {invoice.status === 'VOIDED' ? <InvoiceDocumentBadge status={invoice.status} /> : null}
        </span>
      ),
    },
    {
      key: 'due',
      header: t('payables.fields.dueState'),
      render: (invoice) => <DueStateBadge invoice={invoice} />,
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (invoice) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('payables.openInvoiceNamed', { reference: invoice.reference })}
          onClick={() => void navigate(`/finance/payables/invoices/${invoice.purchaseInvoiceId}`)}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('payables.invoices.title')}
        subtitle={t('payables.invoices.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/finance/payables')}>
              {t('payables.backToDashboard')}
            </Button>
            {canPay ? (
              <Button onClick={() => void navigate('/finance/payables/payments/new')}>
                {t('payables.recordPayment')}
              </Button>
            ) : null}
          </div>
        }
      />

      <Card>
        <CardHeader
          title={t('payables.invoices.title')}
          subtitle={
            data ? t('payables.invoices.count', { count: data.pagination.totalItems }) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            type="search"
            value={search}
            onChange={(event) => onFilter(setSearch)(event.target.value)}
            placeholder={t('payables.invoices.searchPlaceholder')}
            aria-label={t('payables.invoices.searchLabel')}
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allSuppliers') },
              ...(suppliers.data ?? []).map((supplier) => ({
                value: supplier.supplierId,
                label: supplier.supplierName,
              })),
            ]}
            value={supplierId}
            aria-label={t('payables.fields.supplier')}
            onChange={(event) => onFilter(setSupplierId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allBranches') },
              ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
            ]}
            value={branchId}
            aria-label={t('payables.fields.branch')}
            onChange={(event) => onFilter(setBranchId)(event.target.value)}
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allSettlements') },
              ...Object.values(PayableSettlement).map((value) => ({
                value,
                label: t(`payables.settlement.${value}`),
              })),
            ]}
            value={settlement}
            aria-label={t('payables.fields.settlement')}
            onChange={(event) =>
              onFilter(setSettlement)(event.target.value as PayableSettlement | '')
            }
          />
          <Select
            options={[
              { value: '', label: t('payables.filters.allDueStates') },
              ...Object.values(PayableDueState).map((value) => ({
                value,
                label: t(`payables.due.${value}`),
              })),
            ]}
            value={due}
            aria-label={t('payables.fields.dueState')}
            onChange={(event) => onFilter(setDue)(event.target.value as PayableDueState | '')}
          />
          <Select
            options={[
              { value: 'true', label: t('payables.filters.outstandingOnly') },
              { value: 'false', label: t('payables.filters.includeSettled') },
            ]}
            value={outstandingOnly ? 'true' : 'false'}
            aria-label={t('payables.filters.outstandingOnly')}
            onChange={(event) => onFilter(setOutstandingOnly)(event.target.value === 'true')}
          />
          <Input
            type="date"
            value={dueFrom}
            onChange={(event) => onFilter(setDueFrom)(event.target.value)}
            aria-label={t('payables.filters.dueFrom')}
          />
          <Input
            type="date"
            value={dueTo}
            onChange={(event) => onFilter(setDueTo)(event.target.value)}
            aria-label={t('payables.filters.dueTo')}
          />
        </div>

        <DataTable
          caption={t('payables.invoices.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(invoice) => invoice.purchaseInvoiceId}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('payables.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={ReceiptText}
          emptyTitle={t('payables.invoices.empty')}
          emptyDescription={t('payables.invoices.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>
    </div>
  );
}
